import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../contexts/ThemeContext';

const Container = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: ${props => props.theme.cardBg};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.3s ease;
`;

const Title = styled.h1`
  color: ${props => props.theme.primaryText};
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const Subtitle = styled.p`
  color: ${props => props.theme.secondaryText};
  margin-bottom: 2rem;
  transition: color 0.3s ease;
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.primaryText};
  margin-top: 2rem;
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const Paragraph = styled.p`
  color: ${props => props.theme.primaryText};
  line-height: 1.6;
  margin-bottom: 1rem;
  transition: color 0.3s ease;
`;

const Link = styled.a`
  color: ${props => props.theme.primary};
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    text-decoration: underline;
  }
`;

const TermsOfService = () => {
  const { theme } = useTheme();
  
  return (
    <Container theme={theme}>
      <Title theme={theme}>Terms of Service</Title>
      <Subtitle theme={theme}>Last updated: August 2026</Subtitle>

      <SectionTitle theme={theme}>1. Acceptance of Terms</SectionTitle>
      <Paragraph theme={theme}>
        By accessing or using this website ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.
      </Paragraph>

      <SectionTitle theme={theme}>2. No Guarantee of Accuracy</SectionTitle>
      <Paragraph theme={theme}>
        The information, predictions, picks, and content provided on this website are for entertainment and informational purposes only. While we strive to provide accurate and up-to-date information, we make no guarantees regarding the accuracy, completeness, or reliability of any content.
      </Paragraph>

      <SectionTitle theme={theme}>3. Not Gambling Advice</SectionTitle>
      <Paragraph theme={theme}>
        This website does not offer or facilitate gambling, and nothing on this site should be construed as legal, financial, or gambling advice. You are solely responsible for any decisions you make based on the information provided. If you choose to engage in sports betting or gambling, you do so at your own risk and are responsible for complying with all applicable laws in your jurisdiction.
      </Paragraph>

      <SectionTitle theme={theme}>4. No Liability for Losses</SectionTitle>
      <Paragraph theme={theme}>
        Under no circumstances shall the owners, operators, or contributors of this website be liable for any direct, indirect, incidental, consequential, or punitive damages arising out of your use of, or reliance on, any information or content provided by the Service. This includes, but is not limited to, any financial losses incurred as a result of sports betting or gambling.
      </Paragraph>

      <SectionTitle theme={theme}>5. Age Restriction</SectionTitle>
      <Paragraph theme={theme}>
        This website is intended for users who are 18 years of age or older, or the legal age for gambling in your jurisdiction, whichever is greater. By using this Service, you affirm that you meet the minimum age requirement.
      </Paragraph>

      <SectionTitle theme={theme}>6. Accounts</SectionTitle>
      <Paragraph theme={theme}>
        Some features of the Service require an account. When you create an account, you agree to provide accurate information and to keep your login credentials confidential. You are responsible for all activity that occurs under your account.
      </Paragraph>
      <Paragraph theme={theme}>
        We reserve the right to suspend or terminate accounts that violate these Terms, abuse the Service, attempt to circumvent access controls, or engage in conduct that harms the Service or other users.
      </Paragraph>

      <SectionTitle theme={theme}>7. Communications</SectionTitle>
      <Paragraph theme={theme}>
        If you create an account, we may send you service-related messages and, where you have opted in or not opted out, email or push notifications about picks, results, or product updates. You can manage email and push preferences in your account profile, or contact us to unsubscribe.
      </Paragraph>

      <SectionTitle theme={theme}>8. Simulated and Tracked Picks</SectionTitle>
      <Paragraph theme={theme}>
        The Service may allow you to track or simulate picks using hypothetical stakes and a simulated bankroll. These features are for entertainment and informational purposes only. Simulated or tracked picks are not real wagers placed by SlickPicks on your behalf, and they do not constitute betting activity through this website.
      </Paragraph>

      <SectionTitle theme={theme}>9. Intellectual Property</SectionTitle>
      <Paragraph theme={theme}>
        All content on the Service—including predictions, picks, model outputs, text, graphics, logos, and software—is owned by or licensed to SlickPicks and is protected by applicable intellectual property laws. You may use the Service for your personal, non-commercial use only.
      </Paragraph>
      <Paragraph theme={theme}>
        You may not copy, scrape, redistribute, republish, sell, or otherwise commercially exploit our content or data without our prior written permission.
      </Paragraph>

      <SectionTitle theme={theme}>10. Responsible Gambling</SectionTitle>
      <Paragraph theme={theme}>
        Sports betting involves risk and is not appropriate for everyone. If you or someone you know has a gambling problem, help is available. Call 1-800-GAMBLER.
      </Paragraph>

      <SectionTitle theme={theme}>11. Changes to Terms</SectionTitle>
      <Paragraph theme={theme}>
        We reserve the right to modify these Terms at any time. Changes will be effective when posted on this page with an updated date. Your continued use of the Service after changes are posted constitutes your acceptance of the revised Terms.
      </Paragraph>

      <SectionTitle theme={theme}>12. Governing Law</SectionTitle>
      <Paragraph theme={theme}>
        These Terms are governed by the laws of the United States, without regard to conflict-of-law principles. Any dispute arising out of or relating to these Terms or the Service shall be resolved in a court of competent jurisdiction in the United States.
      </Paragraph>

      <SectionTitle theme={theme}>13. Contact</SectionTitle>
      <Paragraph theme={theme}>
        If you have any questions about these Terms, please contact us at <Link theme={theme} href="mailto:cx@slickpicks.bet">cx@slickpicks.bet</Link>.
      </Paragraph>
    </Container>
  );
};

export default TermsOfService;
